import React, {
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from 'data/root.reducers';
import { Contact, ContactGridData, ContactPrivileges } from 'data/contact/contact.types';
import {
  AuditDetails, GridSettings, WithAuditDetails,
} from 'data/types';
import * as selectors from 'data/contact/contact.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/contact/contact.actions';
import * as userActions from 'users/users-actions';
import { AppToaster } from 'shared/toaster';
import ModalDialog from 'shared/modal/dialog';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import ContactGrid from './components/ContactGrid';
import {
  contactColumnDefs,
  contactColumnDefsVersion,
  ContactMenuActionsType,
} from '../../Contact.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  privileges: authenticationSelectors.getAuthenticationPrivileges(state),
  beforeLogout: authenticationSelectors.getAuthenticationBeforeLogout(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  data: selectors.getContacts(state),
  selectedContact: selectors.getSelectedContact(state),
});

const mapDispatch = {
  getAllContacts: actions.getAllContacts,
  goToContactCreate: actions.goToContactCreate,
  goToContactEdit: actions.goToContactEdit,
  selectContact: actions.selectContact,
  goToContactView: actions.goToContactView,
  deleteContact: actions.deleteContact,
  getGridSettings: userActions.getUserSettings,
  saveGridSettings: userActions.putUserSettings,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

const USER_CATEGORY_SETTINGS = 'ContactSettings';

export const ContactList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  privileges,
  deliveryFunctionId,
  data = [],
  beforeLogout,
  selectedContact,
  history,
  getAllContacts,
  getGridSettings,
  saveGridSettings,
  goToContactCreate,
  goToContactEdit,
  selectContact,
  deleteContact,
  goToContactView,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [contactGridData, setContactGridData] =
    useState<ContactGridData[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const { t } = useTranslation();

  useEffect(() => {
    getAllContacts(deliveryFunctionId);

    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getAllContacts,
  ]);

  useEffect(() => {
    setLastUpdatedAt(new Date());

    setContactGridData(data);
  }, [data]);

  const handleRefresh = () => getAllContacts(deliveryFunctionId);

  const handleCreateNew = () => goToContactCreate(history);

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedRows: ContactGridData[] = event.api.getSelectedRows();

    const newSelectedContact = selectedRows.map(({ id }) => {
      return data.find((item) => item.id === id);
    });

    selectContact(newSelectedContact as Contact[]);
  };

  const handleGetGridSettings = () => getGridSettings(USER_CATEGORY_SETTINGS, userName);

  const handleSaveGridSettings = (gridSettings: GridSettings) =>
    saveGridSettings(USER_CATEGORY_SETTINGS, userName, gridSettings);

  const handleContactEdit = () => selectedContact && goToContactEdit(history);

  const handleContactView = () => selectedContact && goToContactView(history);

  const handleGoToContact = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (event.type === ContactMenuActionsType.Delete) {
      setIsDeleteDialogOpen(true);
    } else if (event.type === ContactMenuActionsType.Edit) {
      handleContactEdit();
    } else {
      handleContactView()
    }
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleDelete = () => {
    handleContactDelete();
    setIsDeleteDialogOpen(false);
  };

  const handleContactDelete = () => {
    const contact = selectedContact[0];

    if (contact?.id == null) {
      return;
    }

    const contactBody: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        contact.name,
        AuditService.CONTACT,
        AuditActionType.CONTACT_DELETE,
      );
      contactBody.auditDetails = auditDetails as AuditDetails;
    }

    deleteContact(deliveryFunctionId, contact.id);
  };

  const hasPrivilegesToCreate = privileges.includes(ContactPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(ContactPrivileges.Delete);
  const hasPrivilegeToView = privileges.includes(ContactPrivileges.View);

  return (
    <div data-testid="ContactList" style={{ height: 'inherit' }}>
      <ContactGrid
        selectedContact={selectedContact?.[0]}
        data={contactGridData}
        columnDefs={contactColumnDefs}
        columnDefsVersion={contactColumnDefsVersion}
        noResults={false}
        beforeLogout={beforeLogout}
        lastUpdatedAt={lastUpdatedAt}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        hasPrivilegeToView={hasPrivilegeToView}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onGetGridSettings={handleGetGridSettings}
        onSaveGridSettings={handleSaveGridSettings}
        onSelectionChanged={handleSelectionChanged}
        onEdit={handleContactEdit}
        onDelete={handleContactDelete}
        onSelectItemMenuAction={handleGoToContact}
        suppressAggFuncInHeader={false}
        groupSelectsChildren={false}
        suppressRowClickSelection={false}
        enableGroupSelection={false}
      />
      <ModalDialog
        width="500px"
        isOpen={isDeleteDialogOpen}
        title={t('contact.deletePopup.title')}
        displayMessage={t('contact.deletePopup.message', { name: selectedContact?.[0]?.name })}
        actionText={t('contact.deletePopup.action')}
        onSubmit={handleDelete}
        onClose={handleCloseDeleteDialog}
      />
    </div>
  );
};

export default withRouter(connector(ContactList));
